<script>
import {useMenuStore} from "@js/store/MenuStore.js";
import IconAngleLeft from "@js/components/Icons/IconAngleLeft.vue";
import {Link, router} from "@inertiajs/vue3";
import {computed, onBeforeUnmount, ref, watch} from "vue";

export default {
    name: "TopBarCatalogDropdown",
    components: {IconAngleLeft, Link},

    methods: {
        buildMenuLink(item) {
            if (item?.url) {
                return item.url;
            }

            if (!item?.route_name) {
                return null;
            }

            const fullParams = item.route_params ?? {};
            const {query, ...pureRouteParams} = fullParams;

            let url = this.route(item.route_name, pureRouteParams);

            if (query && typeof query === "object") {
                const queryParams = new URLSearchParams();

                for (const [key, value] of Object.entries(query)) {
                    if (value === null || value === undefined) continue;

                    if (Array.isArray(value) || typeof value === "object") {
                        queryParams.set(key, JSON.stringify(value));
                    } else {
                        queryParams.set(key, String(value));
                    }
                }

                const queryString = queryParams.toString();

                if (queryString) {
                    url += url.includes('?') ? `&${queryString}` : `?${queryString}`;
                }
            }

            return url;
        },

        hasLink(item) {
            return !!(item?.url || item?.route_name);
        }
    },

    setup() {
        const menuStore = useMenuStore();
        const menuRef = ref(null);

        const activeCategory = computed(() => {
            return menuStore.menuItems?.[menuStore.activeCatalog] ?? null;
        });

        const handleClickOutside = (event) => {
            if (menuStore.isMenuCatalogOpen && menuRef.value && !menuRef.value.contains(event.target)) {
                menuStore.closeMenuCatalog();
            }
        };

        watch(
            () => menuStore.isMenuCatalogOpen,
            (isOpen) => {
                if (typeof document === "undefined") return;

                if (isOpen) {
                    setTimeout(() => {
                        document.addEventListener("click", handleClickOutside);
                    }, 50);
                } else {
                    document.removeEventListener("click", handleClickOutside);
                }
            }
        );

        onBeforeUnmount(() => {
            if (typeof document !== "undefined") {
                document.removeEventListener("click", handleClickOutside);
            }

            menuStore.closeMenuCatalog();
        });

        router.on("before", () => {
            menuStore.closeMenuCatalog();
        });

        return {
            menuStore,
            menuRef,
            activeCategory,
        };
    },
};
</script>

<template>
    <nav
        v-show="menuStore.isMenuCatalogOpen"
        ref="menuRef"
        class="absolute inset-x-0 z-20 border-y border-gray-200 bg-white shadow-xs"
    >
        <div class="container">
            <div class="flex min-h-[520px] w-full">
                <div class="w-[320px] shrink-0 border-r border-gray-200 bg-white">
                    <component
                        :is="hasLink(category) ? 'Link' : 'div'"
                        v-for="(category, key) in menuStore.menuItems"
                        :key="key"
                        :href="hasLink(category) ? buildMenuLink(category) : undefined"
                        @mouseenter="menuStore.changeActiveCatalogMenu(key)"
                        class="block cursor-pointer"
                    >
                        <div
                            class="flex items-center justify-between gap-3 px-4 py-4 transition"
                            :class="menuStore.activeCatalog === key ? 'bg-grayBg' : 'md:hover:bg-grayBg'"
                        >
                            <div class="flex min-w-0 items-center gap-3">
                                <img
                                    v-if="category.icon"
                                    :src="category.icon"
                                    :alt="category.title"
                                    class="h-9 w-9 shrink-0 rounded object-cover"
                                />

                                <div class="truncate text-[15px] font-medium text-gray-900">
                                    {{ category.title }}
                                </div>
                            </div>

                            <IconAngleLeft class="shrink-0 rotate-180 text-gray-500"/>
                        </div>
                    </component>
                </div>

                <div class="flex-1 bg-white px-6 py-5">
                    <template v-if="activeCategory">
                        <template v-if="activeCategory.sub_menu?.length">
                            <div
                                class="grid gap-x-8 gap-y-8"
                                :class="[
                                    activeCategory.sub_menu.length >= 4
                                        ? 'grid-cols-4'
                                        : activeCategory.sub_menu.length === 3
                                            ? 'grid-cols-3'
                                            : activeCategory.sub_menu.length === 2
                                                ? 'grid-cols-2'
                                                : 'grid-cols-1'
                                ]"
                            >
                                <div
                                    v-for="(group, groupIndex) in activeCategory.sub_menu"
                                    :key="groupIndex"
                                    class="min-w-0"
                                >
                                    <div class="mb-3 border-b border-gray-200 pb-3">
                                        <component
                                            :is="hasLink(group) ? 'Link' : 'div'"
                                            :href="hasLink(group) ? buildMenuLink(group) : undefined"
                                            class="text-[18px] font-semibold text-gray-900 transition md:hover:text-navHoverColor"
                                        >
                                            {{ group.title }}
                                        </component>
                                    </div>

                                    <div
                                        v-if="group.sub_menu?.length"
                                        class="flex flex-col gap-2"
                                    >
                                        <component
                                            :is="hasLink(item) ? 'Link' : 'div'"
                                            v-for="(item, itemIndex) in group.sub_menu"
                                            :key="itemIndex"
                                            :href="hasLink(item) ? buildMenuLink(item) : undefined"
                                            class="block text-sm leading-snug text-gray-800 transition md:hover:text-navHoverColor"
                                        >
                                            {{ item.title }}
                                        </component>
                                    </div>
                                </div>
                            </div>
                        </template>

                        <template v-else>
                            <div class="flex h-full items-center justify-center text-sm text-gray-400">
                                Пока нет товаров
                            </div>
                        </template>
                    </template>
                </div>
            </div>
        </div>
    </nav>
</template>

<style scoped>
</style>
